import React from "react";
import { Link } from "react-router-dom";
import "../../Styles/ann-tokens.css";
import "../../Styles/marketing-base.css";
import "../../Styles/subpage.css";
import "../../Styles/pages/trust.css";
import useReveal from "./useReveal";

export default function Trust() {
  const ref = useReveal();

  return (
    <div className="page ann-subpage" ref={ref}>
      <section className="sub-hero" id="top">
        <span className="eye">Trust &amp; security</span>
        <h1>Hiring data is the most sensitive data there is. We treat it that way<span className="period" /></h1>
        <p className="lede-xl">
          Ann handles people's careers and your candidates' personal information. <b>That comes with obligations we take seriously</b> — in how data is stored, who sees it, and how every decision is made and explained.
        </p>
        <div className="ctas">
          <a href="#commitments" className="btn btn-primary btn-lg">Our commitments</a>
          <a href="#fairness" className="btn btn-secondary btn-lg">Fair by design</a>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="sub-section">
        <div className="sub-head">
          <span className="eye">The short version</span>
          <h2 className="sub-h2">Three promises behind <em>everything</em>.</h2>
        </div>
        <div className="pillars">
          <div className="pillar">
            <div className="ic" />
            <h3>Your data stays <em>yours</em>.</h3>
            <p>Candidate data lives in your dedicated, encrypted workspace. We never use it to train shared models, and we never sell or share it. Your pool is your pool.</p>
          </div>
          <div className="pillar">
            <div className="ic" />
            <h3>Decisions you can <em>see into</em>.</h3>
            <p>Every score Ann gives is explainable and traceable to the evidence behind it. No black box — tap any candidate and see exactly what drove the result.</p>
          </div>
          <div className="pillar">
            <div className="ic" />
            <h3>Judged on the <em>work</em>, only.</h3>
            <p>Ann evaluates against the role you approve — not names, photos, or schools-as-proxy. Fairness isn't a setting; it's how the evaluation is built.</p>
          </div>
        </div>
      </section>

      {/* DATA COMMITMENTS */}
      <section className="sub-section bone" id="commitments">
        <div className="sub-head">
          <span className="eye">Data &amp; security</span>
          <h2 className="sub-h2">What we commit to, in <em>plain English</em>.</h2>
          <p className="lede">The detail lives in our <Link to="/privacy-policy" className="inline" style={{ color: "oklch(0.45 0.10 200)", textDecoration: "underline" }}>Privacy Policy</Link> and <a href="#" className="inline" style={{ color: "oklch(0.45 0.10 200)", textDecoration: "underline" }}>Data Processing Addendum</a>. Here's the substance.</p>
        </div>
        <div className="commit">
          <div className="c"><div className="k">Encryption</div><div className="v"><b>Encrypted in transit and at rest.</b> Candidate data is protected with industry-standard encryption everywhere it travels and everywhere it sits.</div></div>
          <div className="c"><div className="k">Access control</div><div className="v"><b>Only your invited team.</b> Each workspace is walled off; access is role-based and logged. No other customer — and no unauthorised Ann staff — can see your candidates.</div></div>
          <div className="c"><div className="k">No training on your data</div><div className="v"><b>Your data is never used to train shared models.</b> What Ann learns about your roles stays in your workspace. Your candidates never become someone else's search results.</div></div>
          <div className="c"><div className="k">Data residency</div><div className="v"><b>Regional residency on request.</b> We support keeping your data in a specific region — including the EU and India — where your obligations require it.</div></div>
          <div className="c"><div className="k">Sub-processors</div><div className="v"><b>A short, published list.</b> The third parties that help us run Ann are documented, vetted, and bound by data-protection terms. We give notice before it changes.</div></div>
          <div className="c"><div className="k">Deletion &amp; portability</div><div className="v"><b>Your data leaves when you do.</b> Export it or have it deleted on request, in line with the rights described in our Privacy Policy.</div></div>
        </div>
      </section>

      {/* FAIRNESS */}
      <section className="sub-section" id="fairness">
        <div className="cols-split">
          <div className="sub-head" style={{ marginBottom: 0 }}>
            <span className="eye">Fairness &amp; oversight</span>
            <h2 className="sub-h2">Fair by <em>design</em>, human by <em>default</em>.</h2>
          </div>
          <div className="prose">
            <p className="big">Ann is built to make hiring more evidence-based — which is also what makes it fairer. The two aren't in tension.</p>
            <p><b>Scored on the role, nothing else.</b> Ann evaluates candidates against the criteria you approve. It does not score on names, photos, age signals, or school-as-proxy for ability.</p>
            <p><b>Explainable, every time.</b> Every result traces to the evidence behind it. A decision you can't explain isn't one Ann will hand you.</p>
            <p><b>The human decides.</b> Ann recommends; it never makes the hire. A person reviews and signs off — and that review is part of the record.</p>
            <p><b>Reviewed and improved.</b> We test our evaluations for fairness, document our methodology, and correct what we get wrong. This is ongoing work, and we treat it that way.</p>
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <section className="sub-section bone">
        <div className="sub-head">
          <span className="eye">Compliance posture</span>
          <h2 className="sub-h2">Where we <em>stand</em>.</h2>
          <p className="lede">We build to recognised standards and stay current as hiring regulation evolves worldwide.</p>
        </div>
        <div className="certs">
          <div className="cert"><div className="name">GDPR &amp; UK GDPR</div><div className="st live"><span className="dot" />Aligned · DPA available</div></div>
          <div className="cert"><div className="name">India DPDPA</div><div className="st live"><span className="dot" />Aligned</div></div>
          <div className="cert"><div className="name">SOC 2 Type II</div><div className="st prog"><span className="dot" />In progress</div></div>
          <div className="cert"><div className="name">Evaluation methodology</div><div className="st live"><span className="dot" />Documented · shared on request</div></div>
        </div>
        <p className="prose" style={{ marginTop: 28, fontSize: 13, color: "var(--ink-4)", fontFamily: "var(--f-mono)", letterSpacing: ".04em" }}>// Ann supports your compliance obligations; it does not constitute legal advice. Status reflects current posture and is updated as it changes.</p>
      </section>

      {/* CONTACT */}
      <section className="sub-section">
        <div className="sub-head" style={{ marginBottom: 24 }}>
          <span className="eye">Talk to us</span>
          <h2 className="sub-h2">Security review, DPA, or a <em>question</em>?</h2>
        </div>
        <div className="prose">
          <p>Evaluating Ann for a security or procurement review? We're glad to walk through architecture, sign your DPA, and answer a questionnaire. Reach us at <a href="#" className="inline">[email&#160;protected]</a> and we'll route you to the right person.</p>
        </div>
      </section>

      {/* CTA */}
      <section className="sub-cta">
        <span className="eye">Built to be trusted</span>
        <h2>See it on your <em>own</em> data.</h2>
        <p className="sub">A demo runs on a real role with your safeguards in place. Bring your security questions — we'll answer them live.</p>
        <div className="ctas">
          <Link to="/contact-us" className="btn btn-primary btn-lg">Book a demo</Link>
          <Link to="/privacy-policy" className="btn btn-secondary btn-lg">Read the privacy policy</Link>
        </div>
      </section>
    </div>
  );
}
